import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import BigCalendar from 'react-big-calendar';
import Toolbar from 'react-big-calendar/lib/Toolbar';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faUsers } from '@fortawesome/free-solid-svg-icons';
import CalendarPopup from './CalendarPopup';
import JobInfo from './JobInfo';
import { monthYearFullFormat, timeFormatShort } from '../../../../../constants/date';
import { getEstCostValue } from '../../../../../helpers/general';
import { resolveDateTime } from '../../../../../helpers/jobs';
import { daysDiffForMultipleJobs, prevMonthNavLabel, nextMonthNavLabel } from '../../../../../constants/jobs';
import UsersService from '../../../../../services/UsersService';

import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = BigCalendar.momentLocalizer(moment);

const startOfToday = moment().startOf('day');

export default class JobSchedule extends Component {
  static propTypes = {
    jobsList: PropTypes.array.isRequired,
    jobCostRate: PropTypes.oneOfType([
      PropTypes.string, // in case when field is empty it will be equal to empty string
      PropTypes.number
    ]),
    companyDetails: PropTypes.shape({
      ltaWorkers: PropTypes.array.isRequired,
      favAndBlocks: PropTypes.array.isRequired
    }),
    isLta: PropTypes.bool.isRequired,
    selectedWorkersInfoIsShown: PropTypes.bool.isRequired,
    selectedEventData: PropTypes.object,
    setJobsList: PropTypes.func.isRequired,
    setCalendarRef: PropTypes.func.isRequired
  };

  constructor (props) {
    super(props);
    const { selectedEventData } = props;
    let minAvailableDay = UsersService.isAdmin() ? null : startOfToday;
    let maxAvailableDay = null;
    if (selectedEventData) {
      const { minEventDay, maxEventDay } = this.getAvailableEventDates();
      minAvailableDay = minEventDay;
      maxAvailableDay = maxEventDay;
    }
    this.state = {
      isPopupOpen: false,
      popupData: {},
      minAvailableDay,
      maxAvailableDay
    };
  }

  getAvailableEventDates = () => {
    const { selectedEventData } = this.props;
    const eventStartDate = moment(selectedEventData.start_date);
    let minEventDay = eventStartDate;
    if (eventStartDate.isBefore(startOfToday)) {
      minEventDay = startOfToday;
    }
    return {
      minEventDay,
      maxEventDay: eventStartDate.clone().add(daysDiffForMultipleJobs, 'days')
    };
  };

  getAvailableMinMaxDays = jobsList => {
    const minAvailableDay = jobsList[0].start;
    const maxAvailableDay = jobsList[0].start.clone().add(daysDiffForMultipleJobs, 'days');
    return {
      minAvailableDay,
      maxAvailableDay
    };
  };

  closePopup = () => {
    this.setState({
      isPopupOpen: false
    });
  };

  openPopup = () => {
    this.setState({
      isPopupOpen: true
    });
  };

  isDayOutOfRange = date => {
    const { minAvailableDay, maxAvailableDay } = this.state;
    return date.isBefore(minAvailableDay) || date.isAfter(maxAvailableDay);
  };

  getSortedJobsList = jobsList => jobsList.sort((jobA, jobB) => {
    return jobA.startTime.isBefore(jobB.startTime) ? -1 : 1;
  });

  stylizeCalendarDay = date => {
    let isDisabled = false;
    let isHighlighted = false;
    const day = moment(date).startOf('day');
    const isToday = day.isSame(moment(), 'day');
    if (this.isDayOutOfRange(day)) {
      isDisabled = true;
    }
    this.props.jobsList.forEach(job => {
      if (moment(job.start).isSame(day)) {
        isHighlighted = true;
      }
    });
    return {
      style: {
        // $grayBackground for disabled days and $mint background with opacity for selected days:
        backgroundColor: isHighlighted ? 'rgba(68, 203, 189, 0.2)' : isToday ? '' : isDisabled ? '#edf3f2' : 'white',
        border: isHighlighted ? '1px solid #44cbbd' : '', // $mint border for selected days
        cursor: isDisabled ? 'default' : 'pointer'
      }
    };
  };

  stylizeJobIcon = () => ({
    style: {
      backgroundColor: '#44cbbd', // $mint
      marginRight: '1px'
    }
  });

  selectDate = date => {
    const selectedDate = moment(date).startOf('day');
    if (this.isDayOutOfRange(selectedDate)) {
      return;
    }
    const { popupData } = this.state;

    if (popupData.start) { // for duplicating previously entered data if such is existed
      let startTime = null;
      let endTime = null;
      // use previous time info only for new date and reset for new shift
      if (!popupData.start.isSame(selectedDate, 'day') && popupData.startTime && popupData.endTime) {
        const updatedTimeData = resolveDateTime(selectedDate, popupData.startTime, popupData.endTime);
        startTime = updatedTimeData.startTime;
        endTime = updatedTimeData.endTime;
      }
      this.setState(prevState => ({
        popupData: {
          id: null,
          start: selectedDate,
          end: selectedDate,
          startTime,
          endTime,
          hours: prevState.popupData.hours,
          quantityOfWorkers: prevState.popupData.quantityOfWorkers,
          selectedWorkers: _.cloneDeep(prevState.popupData.selectedWorkers)
        }
      }));
    } else {
      this.setState({
        popupData: { start: selectedDate }
      });
    }
    this.openPopup();
  };

  selectJob = jobData => {
    this.setState({
      popupData: jobData
    });
    this.openPopup();
  };

  updateJobsListData = data => {
    const estCost = getEstCostValue(this.props.jobCostRate, data.hours, data.quantityOfWorkers);
    const jobData = {
      ...data,
      estCost
    };
    const { jobsList, setJobsList } = this.props;
    if (jobsList.some(job => job.id === jobData.id)) {
      jobsList.forEach((job, i) => {
        if (job.id === jobData.id) {
          jobsList[i] = jobData;
        }
      });
    } else {
      jobsList.push(jobData);
    }
    const sortedJobsList = this.getSortedJobsList(jobsList);
    this.setState({
      isPopupOpen: false,
      popupData: data
    });
    setJobsList(this.getSortedJobsList(jobsList));
    if (!this.props.selectedEventData) {
      const { minAvailableDay, maxAvailableDay } = this.getAvailableMinMaxDays(sortedJobsList);
      this.setState({
        minAvailableDay,
        maxAvailableDay
      });
    }
  };

  deleteJobFromList = jobId => {
    const jobsList = this.props.jobsList.filter(job => job.id !== jobId);
    this.props.setJobsList(jobsList);
    if (!this.props.selectedEventData) {
      let minAvailableDay, maxAvailableDay;
      if (jobsList.length === 0) {
        minAvailableDay = UsersService.isAdmin() ? null : startOfToday;
        maxAvailableDay = null;
      } else {
        const minMaxDays = this.getAvailableMinMaxDays(jobsList);
        minAvailableDay = minMaxDays.minAvailableDay;
        maxAvailableDay = minMaxDays.maxAvailableDay;
      }
      this.setState({
        minAvailableDay,
        maxAvailableDay
      });
    }
    this.closePopup();
  };

  render () {
    const { isPopupOpen, popupData } = this.state;
    const { jobsList, setCalendarRef, companyDetails: { ltaWorkers, favAndBlocks }, isLta, selectedWorkersInfoIsShown } = this.props;
    const workersList = isLta ? ltaWorkers : favAndBlocks;
    return (
      <section className='job-posting-section'>
        <header><h4>Job Schedule</h4></header>
        { jobsList.length > 0 ? (
          <div>
            { jobsList.map(job => (
              <JobInfo
                key={job.id}
                jobData={job}
                editJob={this.selectJob}
                deleteJobFromList={this.deleteJobFromList}
                selectedWorkersInfoIsShown={selectedWorkersInfoIsShown}
              />
            ))}
          </div>
        ) : (
          <p>Select a date or multiple dates from the calendar</p>
        )}
        <div className='calendar-wrapper' ref={setCalendarRef}>
          <BigCalendar
            localizer={localizer}
            events={jobsList}
            views={['month']}
            selectable
            formats={{ monthHeaderFormat: monthYearFullFormat }}
            dayPropGetter={this.stylizeCalendarDay}
            eventPropGetter={this.stylizeJobIcon}
            components={{
              event: JobCalendarIcon,
              toolbar: CustomToolbar
            }}
            onDrillDown={this.selectDate}
            onSelectSlot={data => this.selectDate(data.start)}
            onSelectEvent={data => this.selectJob(data)}
            longPressThreshold={1}
          />
          { isPopupOpen && (
            <CalendarPopup
              jobData={popupData}
              closePopup={this.closePopup}
              updateJobsListData={this.updateJobsListData}
              deleteJobFromList={this.deleteJobFromList}
              workersList={workersList}
              isLta={isLta}
            />
          )}
        </div>
      </section>
    );
  }
}

class CustomToolbar extends Toolbar {
  render () {
    const { label } = this.props;
    return (
      <div className='toolbar-wrapper'>
        <div
          className='change-month-arrow'
          onClick={() => this.navigate(prevMonthNavLabel)}
        >
          <FontAwesomeIcon icon={faChevronLeft} size='lg' />
        </div>
        <p>{label}</p>
        <div
          className='change-month-arrow'
          onClick={() => this.navigate(nextMonthNavLabel)}
        >
          <FontAwesomeIcon icon={faChevronRight} size='lg' />
        </div>
      </div>
    );
  }
}

const JobCalendarIcon = ({ event }) => {
  return (
    <div className='calendar-job-preview d-flex justify-content-between'>
      <p>{event.startTime.format(timeFormatShort)}</p>
      <p>
        <span className='mx-1'><FontAwesomeIcon icon={faUsers} /></span>
        {event.quantityOfWorkers}
      </p>
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/components/app/createJob/createJob/LocationScheduleStep/JobSchedule.js